import React from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/ui-components/ui/button';
import { ArrowLeft, Edit, PencilRuler, X, Check, Layout, Download, PenTool } from 'lucide-react';

import { usePostGeneration } from './hooks/usePostGeneration';
import { UploadGraphicDialog } from '../start/components/UploadGraphicDialog';
import { useCreatePostPrefill } from './hooks/useCreatePostPrefill';
import { useIntegration } from './hooks/useIntegration';
import { usePostPublishing } from './hooks/usePostPublishing';

import { SourceSelectionSection } from './components/SourceSelectionSection';
import { PostPreviewSection } from './components/PostPreviewSection';
import { PostActionsSection } from './components/PostActionsSection';
import { useTemplates2FindOne } from '@/lib/api/requests/templates.requests';
import { SubPageLayout } from '@/util-components/page-layout';
import { TemplateNotFound } from './components/TemplateNotFound';

/**
 * Create Post Page - Modern post creation with slot-based entity system
 */
export const CreatePostPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  
  const isCustomUploadParam = !!location.state?.customImageUrl;
  const [customImageUrl, setCustomImageUrl] = React.useState<string | undefined>(location.state?.customImageUrl as string | undefined);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = React.useState(false);
  const isCustomUpload = isCustomUploadParam || !!customImageUrl;
  const [draftSessionId] = React.useState(() => crypto.randomUUID());
  
  // Template loading with API hook
  const {
    data: template,
    isLoading,
    error: templateError
  } = useTemplates2FindOne(templateId || '', {
    enabled: !!templateId && !isCustomUpload, // Only fetch if templateId exists and not custom upload
    skipGlobalErrorHandler: true,
  });
  
  // Post generation hook with slot system - only when template is loaded
  const {
    entitySlots,
    selectorSelections,
    generationState,
    generatedPost,
    generatedImageUrl,
    hasSlotBindings,
    hasSelectorBindings,
    canProceed,
    updateEntitySelection,
    updateSelectorSelection,
    updateManualOverride,
    manualOverrides,
    proceedToNextStep,
    goToPreviousStep,
    generatePost,
    reset
  } = usePostGeneration(template || null);

  // Handle prefilling of entities via URL
  useCreatePostPrefill(entitySlots, updateEntitySelection);

  // Integration & Publishing Hooks (Lifted from PostActionsSection)
  const integrationState = useIntegration();
  const publishingState = usePostPublishing();
  const [imageLoadError, setImageLoadError] = React.useState(false);

  // Trigger generation when entering preview step (if not already generated/generating)
  React.useEffect(() => {
    if (generationState.currentStep === 'preview' && !generatedImageUrl && !generationState.isGenerating && !generationState.error) {
      generatePost();
    }
  }, [generationState.currentStep, generatedImageUrl, generationState.isGenerating, generationState.error, generatePost]);

  /**
   * Navigate back to previous page (caller)
   */
  const handleBack = () => {
    navigate(-1);
  };

  /**
   * Navigate to template editor
   */
  const handleEditTemplate = () => {
    navigate(`/post-generator/template-editor?templateId=${templateId}`);
  };

  /**
   * Handle download action (using generatedImageUrl)
   */
  const handleDownload = () => {
    const urlToDownload = isCustomUpload ? customImageUrl : generatedImageUrl;
    if (urlToDownload) {
        const link = document.createElement('a');
        link.href = urlToDownload;
        link.download = `sportypost-${isCustomUpload ? 'custom' : templateId}-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
  };

  /**
   * Handle share action
   */
  const handleShare = () => {
    console.log('Share functionality not implemented yet');
  };

  // Missing parameters state
  if (!templateId && !isCustomUpload) {
    return (
      <div className="h-screenflex items-center justify-center">
        <UploadGraphicDialog
          isOpen={isUploadDialogOpen}
          onOpenChange={setIsUploadDialogOpen}
          onContinue={(file) => {
            setCustomImageUrl(URL.createObjectURL(file));
            setImageLoadError(false);
            setIsUploadDialogOpen(false);
          }}
        />
        <div className="bg-white p-8 text-center max-w-md rounded-2xl border border-gray-100 shadow-sm">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-gray-100 mx-auto mb-4">
            <Layout className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Kein Post ausgewählt</h3>
          <p className="text-sm text-gray-500 mb-6">
            Bitte wählen Sie ein Template aus oder laden Sie ein eigenes Bild hoch, um einen Post zu erstellen.
          </p>
          <div className="flex flex-col gap-3">
             <Button onClick={() => navigate('/post-generator/start')} className="h-11 px-6 bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-all">
               <ArrowLeft className="h-4 w-4 mr-2" />
               Zurück zur Übersicht
             </Button>
             <Button onClick={() => setIsUploadDialogOpen(true)} variant="outline" className="h-11 px-6">
               Grafik hochladen
             </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoading && !isCustomUpload) {
    return null;
  }

  // Error state - template loading failed or not found
  if (!isCustomUpload && (templateError || (!isLoading && !template && templateId))) {
    return <TemplateNotFound />;
  }

  const { currentStep, isGenerating, error } = generationState;
  
  const displayImageUrl = isCustomUpload ? customImageUrl : generatedImageUrl;
  const displayStep = isCustomUpload ? 'preview' : currentStep;
  const displayIsGenerating = isCustomUpload ? false : isGenerating;

  const showEntitySelection = !isCustomUpload && currentStep === 'selection' && (hasSlotBindings || hasSelectorBindings);
  const showActionsPanel = !showEntitySelection;

  const renderTrailing = () => (
    <>
      {(!isCustomUpload && currentStep !== 'analysis' && (hasSlotBindings || generatedPost)) && (
        <Button 
          variant="outline" 
          size="sm" 
          onClick={reset}
          className="inline-flex h-8 sm:h-10 px-2 sm:px-3 text-xs sm:text-sm text-gray-700 border-gray-200 hover:bg-gray-50 transition-all"
        >
          Zurücksetzen
        </Button>
      )}
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleEditTemplate} 
        className={!isCustomUpload ? "hidden sm:inline-flex h-8 sm:h-10 px-2 sm:px-4 text-xs sm:text-sm text-gray-700 border-gray-200 hover:bg-gray-50 transition-all flex-shrink-0" : "hidden"}
      >
        <PenTool className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />
        <span className="hidden sm:inline">Template bearbeiten</span>
      </Button>
    </>
  );

  return (
    <SubPageLayout
      pageTitle={isCustomUpload ? 'Eigene Grafik posten' : (template?.title || 'Template wird geladen...')}
      parentPageTitle="Post erstellen"
      parentPageCallback={handleBack}
      navigateBackCallback={handleBack}
      icon={<PencilRuler />}
      trailing={renderTrailing()}
      className="lg:overflow-hidden"
    >
      {/* Error display */}
      {error && (
        <div className="bg-red-50 px-6 py-3">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 bg-red-500 flex items-center justify-center">
              <X className="h-2.5 w-2.5 text-white" />
            </div>
            <div className="text-sm font-medium text-red-700">{error}</div>
          </div>
        </div>
      )}

      {/* Upload Dialog when replacing image */}
      <UploadGraphicDialog
        isOpen={isUploadDialogOpen}
        onOpenChange={setIsUploadDialogOpen}
        onContinue={(file) => {
          setCustomImageUrl(URL.createObjectURL(file));
          setImageLoadError(false);
          setIsUploadDialogOpen(false);
        }}
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto h-[100%]" data-onboarding="create-post-page">
        {/* Mobile: Vorschau oben, Auswahl darunter; Desktop: Side-by-side */}
        <div className="flex flex-col lg:flex lg:flex-row lg:h-full">
          {/* Left Panel: Controls - Mobile unten, Desktop links */}
          <div className="order-3 lg:order-1 flex-1 lg:h-full space-y-4 lg:space-y-6 w-full lg:max-w-[500px]" data-onboarding="create-post-selection">
            {/* Entity Selection - Only when slots are present and in selection step */}
            {showEntitySelection && (
              <SourceSelectionSection
                entitySlots={entitySlots}
                onSlotsChange={(slots) => Object.entries(slots).forEach(([key, val]) => updateEntitySelection(key, val.selectedInstance))}
                selectors={selectorSelections}
                onSelectorsChange={(sels) => Object.entries(sels).forEach(([key, val]) => updateSelectorSelection(key, val.selectedValue))}
                onProceedToPreview={proceedToNextStep}
                manualOverrides={manualOverrides}
                onManualOverride={updateManualOverride}
              />
            )}

            {/* Actions Panel - For all other steps */}
            {showActionsPanel && (
              <PostActionsSection
                currentStep={displayStep}
                isSelectionComplete={isCustomUpload ? true : canProceed}
                isGenerating={displayIsGenerating}
                generationResult={isCustomUpload ? null : generatedPost}
                generatedImageUrl={displayImageUrl}
                onProceedToPreview={isCustomUpload ? undefined : proceedToNextStep}
                onBackToSelection={isCustomUpload ? () => setIsUploadDialogOpen(true) : goToPreviousStep}
                onGeneratePost={isCustomUpload ? undefined : generatePost}
                onDownload={handleDownload}
                onShare={handleShare}
                integration={integrationState}
                publishing={publishingState}
                isCustomUpload={isCustomUpload}
                draftSessionId={draftSessionId}
                validationWarning={
                  canProceed || isCustomUpload ? undefined : 
                  (Object.values(entitySlots).some(s => s.missingAttributes && s.missingAttributes.length > 0 && 
                    s.missingAttributes.some(a => !manualOverrides?.[s.slotKey]?.[a.attributePath])
                  )) 
                  ? "Bitte füllen Sie alle markierten Pflichtfelder aus."
                  : undefined
                }
              />
            )}
          </div>

          <div 
            className="order-2 lg:order-2 w-full h-px bg-gray-100 lg:w-px lg:h-auto lg:self-stretch"
            aria-hidden="true"
          />

          {/* Right Panel: Preview - Mobile oben, Desktop rechts */}
          <div
            className="order-1 lg:order-3 flex-shrink-0 w-full lg:w-auto min-h-[300px] lg:min-h-0 lg:h-full flex flex-col bg-white border-l border-gray-100 lg:min-w-[480px]"
            data-onboarding="create-post-preview"
          >
            <div className="flex-1 overflow-hidden relative">
             {isCustomUpload && customImageUrl ? (
                <div className="h-full flex flex-col bg-white overflow-hidden">
                  <div className="flex-none px-4 sm:px-6 py-3 sm:py-4 bg-white flex items-center gap-3 sm:gap-4">
                    <div className="flex h-6 w-6 sm:h-8 sm:w-8 items-center justify-center rounded-lg bg-green-50 border border-green-100">
                      <Check className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                    </div>
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900">Finales Design</h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-0.5">Hochgeladene Grafik</p>
                    </div>
                  </div>
                 <div className="flex-1 flex flex-col items-center justify-start p-4 overflow-hidden min-h-[300px]">
                   {!imageLoadError ? (
                     <div className="w-full h-full max-w-[500px] max-h-[500px] flex items-center justify-center overflow-hidden relative aspect-square sm:aspect-auto mt-4 lg:mt-8">
                        <img 
                          src={customImageUrl} 
                          alt="Custom upload" 
                          onError={() => setImageLoadError(true)}
                          className="max-w-full max-h-full object-contain p-2 sm:p-4 rounded-xl animate-in fade-in zoom-in-[0.99] duration-500 ease-out" 
                        />
                     </div>
                   ) : (
                     <div className="text-center p-8 bg-white rounded-2xl border border-gray-100 shadow-sm max-w-sm mt-4 lg:mt-8">
                       <div className="flex h-12 w-12 items-center justify-center bg-red-50 rounded-xl mx-auto mb-4 border border-red-100">
                         <X className="h-6 w-6 text-red-500" />
                       </div>
                       <h3 className="text-base font-semibold text-gray-900 mb-2">Grafik konnte nicht geladen werden</h3>
                       <p className="text-sm text-gray-500 mb-6">
                         Das Bild ist nicht mehr verfügbar. Dies passiert in der Regel, wenn die Seite neu geladen wird. Bitte wähle das Bild erneut aus.
                       </p>
                       <Button onClick={() => setIsUploadDialogOpen(true)} variant="outline" className="w-full">
                         Zurück und neu hochladen
                       </Button>
                     </div>
                   )}
                 </div>
               </div>
            ) : template ? (
              <PostPreviewSection
                template={template}
                generationResult={generatedPost}
                generatedImageUrl={generatedImageUrl}
                isGenerating={isGenerating}
                entitySlots={entitySlots}
                selectorSelections={selectorSelections}
                manualOverrides={manualOverrides}
                currentStep={currentStep}
                hasDynamicContent={hasSlotBindings || hasSelectorBindings}
              />
            ) : null}
            </div>
            {/* Preview Footer Actions */}
            {(isCustomUpload || currentStep === 'preview' || currentStep === 'generation') && !publishingState.isSuccess && (
              <div className="px-4 pb-3 sm:pb-4 sm:px-6 bg-white flex items-center justify-between gap-3 shrink-0">
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  disabled={!displayImageUrl}
                  className="flex-1 h-10 sm:h-11 px-2 sm:px-6 border-gray-200 text-gray-600 sm:text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg sm:rounded-xl text-sm font-medium shadow-none sm:shadow-sm transition-all"
                >
                  <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2 text-gray-400 sm:text-gray-500" />
                  <span className="truncate">Bild herunterladen</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => isCustomUpload ? setIsUploadDialogOpen(true) : goToPreviousStep()}
                  className="flex-1 h-10 sm:h-11 px-2 sm:px-6 border-gray-200 text-gray-600 sm:text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg sm:rounded-xl text-sm font-medium shadow-none sm:shadow-sm transition-all"
                >
                  <Edit className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-2 text-gray-400 sm:text-gray-500 flex-shrink-0" />
                  <span className="truncate">{isCustomUpload ? 'Anderes Bild' : 'Inhalte ändern'}</span>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default CreatePostPage;
